/**
 * TaskService Utility Functions
 * Diese Datei enthält Hilfsfunktionen für TasksService die nicht von der OpenAPI-Generierung überschrieben werden.
 */

import { TasksService } from './openapi/services/TasksService';
import type { TaskDocument } from './openapi/models/TaskDocument';
import type { TaskImage } from './openapi/models/TaskImage';
import { OpenAPI } from './openapi/core/OpenAPI';
import { request as __request } from './openapi/core/request';
import type { CancelablePromise } from './openapi/core/CancelablePromise';

/**
 * TaskDocument with additional task information for rapport listing
 */
export type BillingStatus = 'OPEN' | 'BILLED' | 'CLOSED';

export interface TaskDocumentWithTask extends TaskDocument {
  document_type: string;  // RAPPORT, OTHER
  task_title: string;
  task_status?: string;
  task_created_at?: string;
  bauvorhaben_id?: number;
  billing_status?: BillingStatus;
}

export class TaskServiceUtils {
  /**
   * Überprüft ob ein Dateityp für Dokumente unterstützt wird
   */
  static isSupportedDocumentType(file: File): boolean {
    const supportedTypes = [
      // PDF
      'application/pdf',
      // Microsoft Office
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      // Text files
      'text/plain',
      'text/csv',
      'application/json',
      'application/xml',
      'text/xml',
      // Archives
      'application/zip',
      'application/x-rar-compressed',
      'application/x-7z-compressed',
      'application/x-tar',
      'application/gzip',
    ];

    const supportedExtensions = [
      'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
      'txt', 'csv', 'json', 'xml', 'zip', 'rar', '7z', 'tar', 'gz'
    ];

    // Check MIME type
    if (supportedTypes.includes(file.type)) {
      return true;
    }

    // Check file extension as fallback
    const extension = this.getFileExtension(file.name, file.type).toLowerCase();
    return supportedExtensions.includes(extension);
  }

  /**
   * Extrahiert die Dateierweiterung aus Dateiname oder MIME-Type
   */
  static getFileExtension(fileName: string, mimeType?: string): string {
    // Zuerst versuche aus dem Dateinamen
    const extensionFromName = fileName.split('.').pop()?.toLowerCase() || '';
    if (extensionFromName) {
      return extensionFromName;
    }

    // Fallback auf MIME-Type
    if (mimeType) {
      const mimeToExtension: { [key: string]: string } = {
        'application/pdf': 'pdf',
        'application/msword': 'doc',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
        'application/vnd.ms-excel': 'xls',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
        'application/vnd.ms-powerpoint': 'ppt',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
        'text/plain': 'txt',
        'text/csv': 'csv',
        'application/json': 'json',
        'application/xml': 'xml',
        'text/xml': 'xml',
        'application/zip': 'zip',
        'application/x-rar-compressed': 'rar',
        'application/x-7z-compressed': '7z',
      };
      return mimeToExtension[mimeType] || '';
    }

    return '';
  }

  /**
   * Formatiert Dateigröße in lesbarem Format
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Lädt ein Dokument herunter
   */
  static async downloadDocumentFile(document: TaskDocument): Promise<void> {
    try {
      const baseUrl = import.meta.env.DEV
        ? 'http://localhost:8008'
        : import.meta.env.VITE_BACKEND_URL || '';
      
      const documentUrl = `${baseUrl}${document.document_path}`;
      
      const response = await fetch(documentUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const linkElement = window.document.createElement('a');
      linkElement.href = url;
      linkElement.download = document.filename;
      
      window.document.body.appendChild(linkElement);
      linkElement.click();
      window.document.body.removeChild(linkElement);
      
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      throw error;
    }
  }

  /**
   * Lädt mehrere Dokumente als ZIP herunter (vereinfachte Version)
   */
  static async bulkDownloadDocuments(documents: TaskDocument[]): Promise<void> {
    // Für jetzt einfach sequentiell herunterladen
    // In Zukunft könnte man hier ein ZIP erstellen
    for (const document of documents) {
      await this.downloadDocumentFile(document);
      // Kleine Pause zwischen Downloads
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  }

  /**
   * Ersetzt ein Task-Bild (Fallback-Implementation)
   */
  static async replaceTaskImage(taskId: number, imageId: number, body: { file: File }): Promise<TaskImage> {
    // Da die replaceTaskImage Methode nicht im Backend existiert,
    // implementieren wir es als delete + upload
    await TasksService.deleteTaskImageTaskTaskIdImagesImageIdDelete(taskId, imageId);
    return await TasksService.uploadTaskImageTaskTaskIdImagesPost(taskId, body);
  }

  /**
   * Holt alle Task-Dokumente mit Task-Informationen
   * @param documentType Filter by document type: 'RAPPORT', 'OTHER', or undefined for all
   */
  static getAllDocuments(documentType?: string): CancelablePromise<TaskDocumentWithTask[]> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/task/documents/all',
      query: {
        document_type: documentType,
      },
      errors: {
        422: 'Validation Error',
      },
    });
  }

  /**
   * Lädt ein Dokument mit spezifischem Dokumenttyp hoch
   * @param taskId Die Task-ID
   * @param file Die hochzuladende Datei
   * @param documentType Der Dokumenttyp: 'RAPPORT' oder 'OTHER' (Standard: 'OTHER')
   */
  static uploadDocumentWithType(taskId: number, file: File, documentType: string = 'OTHER'): CancelablePromise<TaskDocument> {
    const formData = new FormData();
    formData.append('file', file);

    return __request(OpenAPI, {
      method: 'POST',
      url: '/task/{task_id}/documents',
      path: {
        'task_id': taskId,
      },
      query: {
        document_type: documentType,
      },
      body: formData,
      errors: {
        422: 'Validation Error',
      },
    });
  }
}

// Erweitere den TasksService um die Utility-Funktionen
declare module './openapi/services/TasksService' {
  namespace TasksService {
    export const isSupportedDocumentType: typeof TaskServiceUtils.isSupportedDocumentType;
    export const getFileExtension: typeof TaskServiceUtils.getFileExtension;
    export const formatFileSize: typeof TaskServiceUtils.formatFileSize;
    export const downloadDocumentFile: typeof TaskServiceUtils.downloadDocumentFile;
    export const bulkDownloadDocuments: typeof TaskServiceUtils.bulkDownloadDocuments;
    export const replaceTaskImage: typeof TaskServiceUtils.replaceTaskImage;
    export const getAllDocuments: typeof TaskServiceUtils.getAllDocuments;
    export const uploadDocumentWithType: typeof TaskServiceUtils.uploadDocumentWithType;
  }
}

// Füge die Methoden dem TasksService hinzu
Object.assign(TasksService, {
  isSupportedDocumentType: TaskServiceUtils.isSupportedDocumentType,
  getFileExtension: TaskServiceUtils.getFileExtension,
  formatFileSize: TaskServiceUtils.formatFileSize,
  downloadDocumentFile: TaskServiceUtils.downloadDocumentFile,
  bulkDownloadDocuments: TaskServiceUtils.bulkDownloadDocuments,
  replaceTaskImage: TaskServiceUtils.replaceTaskImage,
  getAllDocuments: TaskServiceUtils.getAllDocuments,
  uploadDocumentWithType: TaskServiceUtils.uploadDocumentWithType,
});
